import React, { useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton } from "src/components/GlassButtons";
import { ExportData } from "src/managers/ExportData";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { JPBIndexBuilderManager, type JPBIndexBuilderCacheInfo } from "./JPBIndexBuilderManager";
import {
  JPBIndexDocument,
  JPBIndexInterval,
  JPBIndexSmoothingMode,
  JPBIndexStatistic,
} from "./JPBIndexBuilderTypes";
import JPBIndexOfLandPrices from "./JPBIndexOfLandPrices.json";

const formatValue = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "DKK",
    maximumFractionDigits: 2,
  }).format(value);
};

const formatFactor = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return value.toFixed(4);
};

const formatCount = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
};

const formatSignedFactor = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  return `${value >= 0 ? "+" : ""}${value.toFixed(4)}`;
};

const formatRoi = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) return "-";
  const pct = value * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
};

const csvCell = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  const text = typeof value === "string" ? value : String(value);
  const escaped = text.replace(/"/g, "\"\"");
  if (/[",\n]/.test(escaped)) return `"${escaped}"`;
  return escaped;
};

const intervalOptions: { value: JPBIndexInterval; label: string }[] = [
  { value: "quarter", label: "Quarter" },
  { value: "halfYear", label: "Half-year" },
  { value: "fullYear", label: "Full-year" },
];

const statisticOptions: { value: JPBIndexStatistic; label: string }[] = [
  { value: "median", label: "Median" },
  { value: "mean", label: "Mean" },
];

const smoothingOptions: { value: JPBIndexSmoothingMode; label: string }[] = [
  { value: "none", label: "None" },
  { value: "windowPlusMinusOne", label: "Sliding window (±1 interval)" },
];

const LOW_TX_COUNT_LIMIT = 5;
const LOW_TX_PERIOD_COUNT_TO_HIDE = 3;

export function JPBIndexBuilderWindow(props: { managedWindow: ManagedWindow }) {
  const { dispatch: windowDispatch } = useWindowContext();
  const [interval, setInterval] = useState<JPBIndexInterval>("quarter");
  const [statistic, setStatistic] = useState<JPBIndexStatistic>("median");
  const [smoothingMode, setSmoothingMode] = useState<JPBIndexSmoothingMode>("none");
  const [buildingAreaThresholdHa, setBuildingAreaThresholdHa] = useState<number>(50);
  const [subtractBuildingPrices, setSubtractBuildingPrices] = useState<boolean>(true);
  const [yearsBack, setYearsBack] = useState<number>(5);
  const [includeFit, setIncludeFit] = useState<boolean>(true);
  const [fitIntervalMode, setFitIntervalMode] = useState<"none" | "rmse" | "ci95">("none");
  const [documents, setDocuments] = useState<JPBIndexDocument[]>([]);
  const [computedMode, setComputedMode] = useState<"all" | null>(null);
  const [visibleLandsdele, setVisibleLandsdele] = useState<string[]>([]);
  const [visibleAgroLandsdele, setVisibleAgroLandsdele] = useState<string[]>([]);
  const [allMatrixMode, setAllMatrixMode] = useState<"factor" | "txCount" | "fitValue">("factor");
  const [isComputingSelected, setIsComputingSelected] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isExportingRawCsv, setIsExportingRawCsv] = useState<boolean>(false);
  const [progressText, setProgressText] = useState<string>("");
  const [statusText, setStatusText] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");
  const [cacheInfo, setCacheInfo] = useState<JPBIndexBuilderCacheInfo | null>(null);

  const indexName = JPBIndexBuilderManager.getTargetIndexName();
  const isComputedAll = computedMode === "all" && documents.length > 0;

  const allPeriodHeaders = useMemo(() => {
    if (!documents.length) return [];
    const keyMap = new Map<string, number>();
    documents.forEach((doc) => {
      doc.periods.forEach((period) => {
        const existing = keyMap.get(period.periodKey);
        if (existing === undefined || period.periodStart < existing) {
          keyMap.set(period.periodKey, period.periodStart);
        }
      });
    });
    return [...keyMap.entries()]
      .sort((a, b) => a[1] - b[1])
      .map(([key]) => key);
  }, [documents]);

  const allValueFits = useMemo(() => {
    const fits = new Map<string, { a: number; b: number; rSquared: number; rmse: number } | undefined>();
    if (!documents.length) return fits;
    documents.forEach((doc) => {
      fits.set(doc.landsdel, fitExpForValueSeries(doc.periods.map((period) => period.value)));
    });
    return fits;
  }, [documents]);

  const periodsPerYear = useMemo(() => {
    if (interval === "quarter") return 4;
    if (interval === "halfYear") return 2;
    return 1;
  }, [interval]);

  const fitIntervalMultiplier = useMemo(() => {
    if (fitIntervalMode === "rmse") return 1;
    if (fitIntervalMode === "ci95") return 1.96;
    return 0;
  }, [fitIntervalMode]);

  const annualRoiByLandsdel = useMemo(() => {
    const out = new Map<string, number | null>();
    if (!documents.length) return out;
    documents.forEach((doc) => {
      const fit = allValueFits.get(doc.landsdel);
      if (!fit) {
        out.set(doc.landsdel, null);
        return;
      }
      out.set(doc.landsdel, Math.exp(fit.b * periodsPerYear) - 1);
    });
    return out;
  }, [documents, allValueFits, periodsPerYear]);

  const allChartData = useMemo(() => {
    if (computedMode !== "all" || !documents.length) return [];
    const rows = allPeriodHeaders.map((periodKey) => {
      const row: Record<string, string | number | null> = { periodKey };
      documents.forEach((doc) => {
        const period = doc.periods.find((p) => p.periodKey === periodKey);
        row[doc.landsdel] = period?.value ?? null;
        const fit = allValueFits.get(doc.landsdel);
        const periodIdx = doc.periods.findIndex((p) => p.periodKey === periodKey);
        const fitValue = fit && periodIdx >= 0 ? fit.a * Math.exp(fit.b * periodIdx) : null;
        row[`${doc.landsdel}__fit`] = fitValue;
        const fitDelta =
          fit && periodIdx >= 0 && Number.isFinite(fit.rmse) && fitIntervalMultiplier > 0
            ? fit.rmse * fitIntervalMultiplier
            : null;
        const fitUpper = fitValue !== null && fitDelta !== null ? fitValue + fitDelta : null;
        const fitLower = fitValue !== null && fitDelta !== null ? Math.max(0, fitValue - fitDelta) : null;
        row[`${doc.landsdel}__fitUpper`] = fitUpper;
        row[`${doc.landsdel}__fitLower`] = fitLower;
        row[`${doc.landsdel}__fitBand`] =
          typeof fitUpper === "number" && typeof fitLower === "number" ? Math.max(0, fitUpper - fitLower) : null;
      });
      return row;
    });
    return rows;
  }, [computedMode, documents, allPeriodHeaders, allValueFits, fitIntervalMultiplier]);

  const fittedFactorByLandsdel = useMemo(() => {
    const out = new Map<string, Map<string, number | null>>();
    if (!documents.length) return out;

    documents.forEach((doc) => {
      const fit = allValueFits.get(doc.landsdel);
      const idxByKey = new Map(doc.periods.map((period, idx) => [period.periodKey, idx]));
      const baseIdx = doc.basePeriod ? idxByKey.get(doc.basePeriod) : 0;
      const baseFitValue =
        fit && baseIdx !== undefined ? fit.a * Math.exp(fit.b * baseIdx) : null;
      const factors = new Map<string, number | null>();

      doc.periods.forEach((period, idx) => {
        if (!fit || !baseFitValue || !Number.isFinite(baseFitValue) || baseFitValue <= 0) {
          factors.set(period.periodKey, null);
          return;
        }
        const fitValue = fit.a * Math.exp(fit.b * idx);
        factors.set(period.periodKey, fitValue / baseFitValue);
      });
      out.set(doc.landsdel, factors);
    });

    return out;
  }, [computedMode, documents, allValueFits]);

  const agroNormalizedByLandsdel = useMemo(() => {
    type AgroRegion = { name: string; values: Record<string, number> };
    const periods = (JPBIndexOfLandPrices?.periods || []) as string[];
    const regions = (JPBIndexOfLandPrices?.regions || []) as AgroRegion[];
    const out = new Map<string, Record<string, number>>();
    if (!periods.length || !regions.length) return out;

    const firstPeriod = periods[0];
    const regionByName = new Map<string, AgroRegion>();
    regions.forEach((region) => regionByName.set(region.name, region));

    const candidateMap = (landsdel: string): string[] => {
      const exact = `Landsdel ${landsdel}`;
      const fallback: string[] = [];
      // Keep pragmatic fallbacks aligned with existing historical behavior.
      if (landsdel === "Vest- og Sydsjælland" || landsdel === "Sjælland" || landsdel === "Bornholm" || landsdel === "Lolland-Falster") {
        fallback.push("Landsdel Vest- og Sydsjælland");
      }
      return [exact, ...fallback];
    };

    documents.forEach((doc) => {
      const candidates = candidateMap(doc.landsdel);
      const region = candidates.map((name) => regionByName.get(name)).find((v) => Boolean(v));
      if (!region) return;
      const base = region.values[firstPeriod];
      if (!base || !Number.isFinite(base) || base <= 0) return;

      const norm: Record<string, number> = {};
      periods.forEach((periodKey) => {
        const value = region.values[periodKey];
        if (typeof value === "number" && Number.isFinite(value)) {
          norm[periodKey] = value / base;
        }
      });
      out.set(doc.landsdel, norm);
    });

    return out;
  }, [documents]);

  const agroComparableDocuments = useMemo(() => {
    return documents.filter((doc) => agroNormalizedByLandsdel.has(doc.landsdel));
  }, [documents, agroNormalizedByLandsdel]);

  const visibleAllDocuments = useMemo(() => {
    if (computedMode !== "all") return [];
    if (!visibleLandsdele.length) return [];
    const visibleSet = new Set(visibleLandsdele);
    return documents.filter((doc) => visibleSet.has(doc.landsdel));
  }, [computedMode, documents, visibleLandsdele]);

  const visibleAgroComparableLandsdele = useMemo(() => {
    const comparableSet = new Set(agroComparableDocuments.map((doc) => doc.landsdel));
    return visibleAgroLandsdele.filter((landsdel) => comparableSet.has(landsdel));
  }, [visibleAgroLandsdele, agroComparableDocuments]);

  const allChartDataWithAgro = useMemo(() => {
    if (!visibleAgroComparableLandsdele.length) return allChartData;
    if (!allChartData.length) return allChartData;

    const scalingByLandsdel = new Map<string, { agroByPeriod: Record<string, number>; baseValue: number }>();
    visibleAgroComparableLandsdele.forEach((landsdel) => {
      const doc = documents.find((item) => item.landsdel === landsdel);
      const agroByPeriod = agroNormalizedByLandsdel.get(landsdel);
      if (!doc || !agroByPeriod) return;
      const alignPeriod =
        doc.basePeriod ?? doc.periods.find((p) => typeof p.value === "number" && p.value > 0)?.periodKey;
      const rawValueAtAlign =
        alignPeriod ? doc.periods.find((p) => p.periodKey === alignPeriod)?.value : null;
      if (typeof rawValueAtAlign !== "number" || !Number.isFinite(rawValueAtAlign) || rawValueAtAlign <= 0) return;
      const agroFactorAtAlign = alignPeriod ? agroByPeriod[alignPeriod] : 1;
      const baseValue =
        typeof agroFactorAtAlign === "number" && Number.isFinite(agroFactorAtAlign) && agroFactorAtAlign > 0
          ? rawValueAtAlign / agroFactorAtAlign
          : rawValueAtAlign;
      scalingByLandsdel.set(landsdel, { agroByPeriod, baseValue });
    });
    if (!scalingByLandsdel.size) return allChartData;

    return allChartData.map((row) => {
      const periodKey = String(row.periodKey ?? "");
      const withAgro: Record<string, string | number | null> = { ...row };
      scalingByLandsdel.forEach(({ agroByPeriod, baseValue }, landsdel) => {
        const agroFactor = agroByPeriod[periodKey];
        withAgro[`${landsdel}__agro`] =
          typeof agroFactor === "number" && Number.isFinite(agroFactor)
            ? agroFactor * baseValue
            : null;
      });
      return withAgro;
    });
  }, [visibleAgroComparableLandsdele, allChartData, documents, agroNormalizedByLandsdel]);

  const canExport = documents.length > 0 && !isExportingRawCsv;
  const canRunCompute = !isComputingSelected && !isUploading && !isExportingRawCsv;
  const canUploadComputedAll = isComputedAll && !isUploading && !isComputingSelected && !isExportingRawCsv;
  const canExportRawCsv = isComputedAll && !isComputingSelected && !isUploading && !isExportingRawCsv && visibleLandsdele.length > 0;
  const chartStrokePalette = ["#268bd2", "#2aa198", "#cb4b16", "#6c71c4", "#859900", "#d33682", "#b58900", "#073642", "#dc322f"];
  const totalMatrixTransactions = useMemo(() => {
    return documents.reduce(
      (sum, doc) => sum + doc.periods.reduce((docSum, period) => docSum + (period.txCount || 0), 0),
      0
    );
  }, [documents]);

  const runCompute = async (forceRefresh: boolean) => {
    setIsComputingSelected(true);
    setErrorText("");
    setStatusText("");
    setProgressText("");
    const hadPreviousComputedAll = computedMode === "all";
    const previousVisibleLandsdele = visibleLandsdele;
    const previousVisibleAgroLandsdele = visibleAgroLandsdele;
    try {
      const result = await JPBIndexBuilderManager.computeAllDocuments(
        {
          interval,
          statistic,
          smoothingMode,
          buildingAreaThresholdHa,
          subtractBuildingPrices,
          yearsBack,
          includeFit,
        },
        ({ landsdel, current, total }) => {
          setProgressText(`Computing ${landsdel} (${current}/${total})`);
        },
        { forceRefresh }
      );
      setDocuments(result.documents);
      setComputedMode("all");
      const defaultVisible = result.documents
        .filter((doc) => doc.periods.filter((period) => period.txCount < LOW_TX_COUNT_LIMIT).length < LOW_TX_PERIOD_COUNT_TO_HIDE)
        .map((doc) => doc.landsdel);
      const availableLandsdele = new Set(result.documents.map((doc) => doc.landsdel));
      const preservedVisible = previousVisibleLandsdele.filter((landsdel) => availableLandsdele.has(landsdel));
      const nextVisible = hadPreviousComputedAll ? preservedVisible : defaultVisible;
      setVisibleLandsdele(nextVisible);
      const preservedAgroVisible = previousVisibleAgroLandsdele.filter((landsdel) => availableLandsdele.has(landsdel));
      const nextAgroVisible = hadPreviousComputedAll ? preservedAgroVisible : [];
      setVisibleAgroLandsdele(nextAgroVisible);
      setCacheInfo(result.cache);
      const hiddenByDefault = result.documents.length - defaultVisible.length;
      if (hadPreviousComputedAll) {
        setStatusText(
          `Computed ${result.documents.length} landsdele (${result.cache.rowsInMemory} rows, ${result.cache.usedCache ? "cache hit" : "refetched"
          }). Preserved visible curves: ${nextVisible.length}.`
        );
      } else {
        setStatusText(
          `Computed ${result.documents.length} landsdele (${result.cache.rowsInMemory} rows, ${result.cache.usedCache ? "cache hit" : "refetched"
          }). Curves hidden by default: ${hiddenByDefault} (>=${LOW_TX_PERIOD_COUNT_TO_HIDE} periods with txCount < ${LOW_TX_COUNT_LIMIT}).`
        );
      }
      setProgressText("");
    } catch (error: any) {
      setErrorText(error?.message || String(error));
    } finally {
      setIsComputingSelected(false);
    }
  };

  const runComputeFromCache = async () => runCompute(false);
  const runRefreshAndCompute = async () => runCompute(true);

  const runUpload = async () => {
    if (!documents.length) return;
    setIsUploading(true);
    setErrorText("");
    setStatusText("");
    try {
      setProgressText("Uploading to Elasticsearch...");
      const uploadResult = await JPBIndexBuilderManager.uploadDocuments(documents, indexName);
      setStatusText(`Uploaded ${uploadResult.uploaded} documents to ${uploadResult.indexName}.`);
      setProgressText("");
    } catch (error: any) {
      setErrorText(error?.message || String(error));
      setProgressText("");
    } finally {
      setIsUploading(false);
    }
  };

  const exportJson = () => {
    if (!documents.length) return;
    const blob = new Blob([JPBIndexBuilderManager.toExportJSON(documents)], { type: "application/json" });
    ExportData.downloadBlob(`jpb-index-builder-${Date.now()}.json`, blob);
  };

  const exportNdjson = () => {
    if (!documents.length) return;
    const blob = new Blob([JPBIndexBuilderManager.toExportNDJSON(documents, indexName)], { type: "application/x-ndjson" });
    ExportData.downloadBlob(`jpb-index-builder-${Date.now()}.ndjson`, blob);
  };

  const exportCheckedRawCsv = async () => {
    if (!isComputedAll) return;
    const checkedLandsdele = visibleLandsdele.filter((landsdel) => documents.some((doc) => doc.landsdel === landsdel));
    if (!checkedLandsdele.length) {
      setErrorText("No checked landsdele selected. Check at least one landsdel row before exporting raw CSV.");
      return;
    }

    setIsExportingRawCsv(true);
    setErrorText("");
    setStatusText("");
    try {
      setProgressText("Preparing checked raw transactions CSV...");
      const result = await JPBIndexBuilderManager.getRawCheckedRows(
        {
          interval,
          statistic,
          smoothingMode,
          buildingAreaThresholdHa,
          subtractBuildingPrices,
          yearsBack,
          includeFit,
          landsdele: checkedLandsdele,
        },
        { forceRefresh: false }
      );

      const docByLandsdel = new Map(documents.map((doc) => [doc.landsdel, doc]));
      const periodIndexByLandsdel = new Map<string, Map<string, number>>();
      documents.forEach((doc) => {
        periodIndexByLandsdel.set(
          doc.landsdel,
          new Map(doc.periods.map((period, idx) => [period.periodKey, idx]))
        );
      });

      const headers = [
        "checked",
        "landsdel",
        "komkode",
        "dateIso",
        "dateMs",
        "periodKey",
        "m2total",
        "rawSalePrice",
        "rawPricePerM2",
        "prisDriftsbygning",
        "prisStuehus",
        "hasBuildingValue",
        "buildingAreaThresholdHa",
        "subtractBuildingPrices",
        "effectiveRawPrice",
        "effectivePricePerM2",
        "periodStatisticValue",
        "periodFactor",
        "periodTxCount",
        "periodFitValue",
        "periodFitFactor",
      ];
      const lines: string[] = [headers.map(csvCell).join(",")];

      result.rows.forEach((row) => {
        const doc = docByLandsdel.get(row.landsdel);
        const period = doc?.periods.find((p) => p.periodKey === row.periodKey);
        const periodIdx = periodIndexByLandsdel.get(row.landsdel)?.get(row.periodKey);
        const fit = allValueFits.get(row.landsdel);
        const fitValue =
          fit && periodIdx !== undefined ? fit.a * Math.exp(fit.b * periodIdx) : null;
        const fitFactor = fittedFactorByLandsdel.get(row.landsdel)?.get(row.periodKey) ?? null;

        const values = [
          true,
          row.landsdel,
          row.komkode,
          new Date(row.datoMs).toISOString(),
          row.datoMs,
          row.periodKey,
          row.m2total,
          row.rawSalePrice,
          row.rawPricePerM2,
          row.prisDriftsbygning,
          row.prisStuehus,
          row.hasBuildingValue,
          row.buildingAreaThresholdHa,
          row.subtractBuildingPrices,
          row.effectiveRawPrice,
          row.effectivePricePerM2,
          period?.value ?? null,
          period?.factor ?? null,
          period?.txCount ?? null,
          fitValue,
          fitFactor,
        ];
        lines.push(values.map(csvCell).join(","));
      });

      const blob = new Blob([lines.join("\n") + "\n"], { type: "text/csv;charset=utf-8;" });
      ExportData.downloadBlob(`jpb-index-builder-raw-checked-${Date.now()}.csv`, blob);
      setStatusText(
        `Exported ${result.rows.length} raw checked rows to CSV (${result.cache.usedCache ? "cache hit" : "refetched"}).`
      );
      setProgressText("");
    } catch (error: any) {
      setErrorText(error?.message || String(error));
      setProgressText("");
    } finally {
      setIsExportingRawCsv(false);
    }
  };

  const toggleLandsdelVisibility = (landsdel: string) => {
    setVisibleLandsdele((prev) => {
      if (prev.includes(landsdel)) {
        return prev.filter((name) => name !== landsdel);
      }
      return [...prev, landsdel];
    });
  };

  const toggleAgroVisibility = (landsdel: string) => {
    setVisibleAgroLandsdele((prev) => {
      if (prev.includes(landsdel)) {
        return prev.filter((name) => name !== landsdel);
      }
      return [...prev, landsdel];
    });
  };

  return (
    <AdvancedDragModal
      title="JPB Index Builder"
      window={props.managedWindow}
      allowUserMiniMize={true}
      allowFullscreen={true}
      PositionToSave={{ left: true, top: true, width: true, height: true }}
      PosDefault={{
        top: "12px",
        left: "calc(100% - 12px)",
        transform: "translateX(-100%)",
        width: "980px",
      }}
      onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
    >
      <ADModalBody>
        <div style={{ padding: "12px", maxHeight: "calc(100vh - 120px)", overflowY: "auto" }}>
          <div style={{ marginBottom: "10px", fontSize: "12px", opacity: 0.85 }}>
            Target index: <strong>{indexName}</strong>. Upload is enabled after <strong>Compute all</strong>.
          </div>

          <div
            style={{
              marginBottom: "10px",
              padding: "8px",
              border: "1px solid rgba(255,255,255,0.08)",
              background: "rgba(255,255,255,0.02)",
            }}
          >
            <div style={{ fontSize: "12px", opacity: 0.8, marginBottom: "6px" }}>
              Data scope (this setting changes fetch window and may require refetch)
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "220px auto", gap: "8px", alignItems: "end" }}>
              <label style={{ display: "block" }}>
                <div style={{ fontSize: "12px", marginBottom: "4px" }}>Years back</div>
                <input
                  type="number"
                  min={1}
                  max={25}
                  value={yearsBack}
                  onChange={(evt) => setYearsBack(Math.max(1, Math.min(25, Number(evt.target.value) || 1)))}
                  style={{ width: "100%" }}
                />
              </label>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: "10px", alignItems: "end" }}>
            <label style={{ display: "block" }}>
              <div style={{ fontSize: "12px", marginBottom: "4px" }}>Interval</div>
              <select value={interval} onChange={(evt) => setInterval(evt.target.value as JPBIndexInterval)} style={{ width: "100%" }}>
                {intervalOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block" }}>
              <div style={{ fontSize: "12px", marginBottom: "4px" }}>Statistic</div>
              <select value={statistic} onChange={(evt) => setStatistic(evt.target.value as JPBIndexStatistic)} style={{ width: "100%" }}>
                {statisticOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block" }}>
              <div style={{ fontSize: "12px", marginBottom: "4px" }}>Smoothing</div>
              <select
                value={smoothingMode}
                onChange={(evt) => setSmoothingMode(evt.target.value as JPBIndexSmoothingMode)}
                style={{ width: "100%" }}
              >
                {smoothingOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <label style={{ display: "block" }}>
              <div style={{ fontSize: "12px", marginBottom: "4px" }}>Building area threshold (ha)</div>
              <input
                type="number"
                min={0}
                step={1}
                value={buildingAreaThresholdHa}
                onChange={(evt) =>
                  setBuildingAreaThresholdHa(Math.max(0, Math.round(Number(evt.target.value) || 0)))
                }
                style={{ width: "100%" }}
              />
            </label>

            <label style={{ display: "block" }}>
              <div style={{ fontSize: "12px", marginBottom: "4px" }}>Subtract building prices</div>
              <select
                value={subtractBuildingPrices ? "yes" : "no"}
                onChange={(evt) => setSubtractBuildingPrices(evt.target.value === "yes")}
                style={{ width: "100%" }}
              >
                <option value="no">No</option>
                <option value="yes">Yes</option>
              </select>
            </label>

            <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", paddingBottom: "4px" }}>
              <input type="checkbox" checked={includeFit} onChange={(evt) => setIncludeFit(evt.target.checked)} />
              Exponential fit
            </label>

            <label style={{ display: "block" }}>
              <div style={{ fontSize: "12px", marginBottom: "4px" }}>Fit interval</div>
              <select
                value={fitIntervalMode}
                onChange={(evt) => setFitIntervalMode(evt.target.value as "none" | "rmse" | "ci95")}
                style={{ width: "100%" }}
                disabled={!includeFit}
              >
                <option value="none">None</option>
                <option value="rmse">±RMSE</option>
                <option value="ci95">95% (approx)</option>
              </select>
            </label>
          </div>

          <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <GlassButton onClick={runComputeFromCache} disabled={!canRunCompute}>
              {isComputingSelected ? (
                <>
                  <Spinner animation="border" size="sm" style={{ width: "1em", height: "1em", marginRight: "6px" }} />
                  Computing...
                </>
              ) : (
                "Compute all"
              )}
            </GlassButton>
            <GlassButton onClick={runRefreshAndCompute} disabled={!canRunCompute}>
              Refresh data + Compute
            </GlassButton>

            <GlassButton onClick={exportJson} disabled={!canExport}>
              Export JSON
            </GlassButton>
            <GlassButton onClick={exportNdjson} disabled={!canExport}>
              Export NDJSON
            </GlassButton>
            <GlassButton onClick={exportCheckedRawCsv} disabled={!canExportRawCsv}>
              {isExportingRawCsv ? (
                <>
                  <Spinner animation="border" size="sm" style={{ width: "1em", height: "1em", marginRight: "6px" }} />
                  Exporting CSV...
                </>
              ) : (
                "Export checked raw CSV"
              )}
            </GlassButton>
          </div>

          {cacheInfo ? (
            <div style={{ marginTop: "10px", fontSize: "12px", opacity: 0.85 }}>
              Cache: {cacheInfo.usedCache ? "reused in-memory rows" : "refetched rows"} | Rows: {cacheInfo.rowsInMemory} | Years back: {cacheInfo.yearsBack}
            </div>
          ) : null}

          {progressText ? <div style={{ marginTop: "10px", fontSize: "12px", opacity: 0.9 }}>{progressText}</div> : null}
          {statusText ? <div style={{ marginTop: "10px", color: "#2e7d32", fontSize: "13px" }}>{statusText}</div> : null}
          {errorText ? <div style={{ marginTop: "10px", color: "#b71c1c", fontSize: "13px" }}>{errorText}</div> : null}

          {isComputedAll ? (
            <>
              <div style={{ marginTop: "16px", padding: "10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontWeight: 600, marginBottom: "8px" }}>
                  All landsdele - {interval} - {statistic}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.75, marginBottom: "8px" }}>
                  Smoothing: {smoothingMode === "none" ? "None" : "Sliding window (±1 interval)"}
                </div>
                <div style={{ fontSize: "12px", opacity: 0.85, marginBottom: "8px" }}>
                  Total transaction items after filters (matrix sum): <strong>{formatCount(totalMatrixTransactions)}</strong>
                </div>
                <div style={{ fontSize: "12px", opacity: 0.75, marginBottom: "8px" }}>
                  Click rows in the matrix to hide/show landsdele in the graph.
                </div>
                <div style={{ fontSize: "12px", opacity: 0.75, marginBottom: "8px" }}>
                  AgroCura overlays are controlled from the AgroCura table rows below and are off by default.
                </div>
                <div style={{ width: "100%", height: 360 }}>
                  <ResponsiveContainer>
                    <ComposedChart data={allChartDataWithAgro} margin={{ top: 8, right: 24, bottom: 4, left: 4 }}>
                      {includeFit && fitIntervalMode !== "none" && (
                        <defs>
                          {visibleAllDocuments.map((doc, idx) => {
                            const stroke = chartStrokePalette[idx % chartStrokePalette.length];
                            const gradientId = `fitBand_${doc.landsdel.replace(/[^a-zA-Z0-9_-]/g, "_")}_${idx}`;
                            return (
                              <linearGradient key={gradientId} id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={stroke} stopOpacity={0.26} />
                                <stop offset="100%" stopColor={stroke} stopOpacity={0.05} />
                              </linearGradient>
                            );
                          })}
                        </defs>
                      )}
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="periodKey" minTickGap={20} />
                      <YAxis tickFormatter={(v) => Number(v).toFixed(0)} />
                      <Tooltip formatter={(value: number, name: string) => [formatValue(value), formatSeriesLabel(name)]} />
                      <Legend />
                      {visibleAllDocuments.map((doc, idx) => (
                        <React.Fragment key={doc.landsdel}>
                          <Line
                            type="monotone"
                            dataKey={doc.landsdel}
                            stroke={chartStrokePalette[idx % chartStrokePalette.length]}
                            dot={false}
                            connectNulls={false}
                            name={doc.landsdel}
                          />
                          {includeFit && (
                            <>
                              {fitIntervalMode !== "none" && (
                                <>
                                  <Area
                                    type="monotone"
                                    dataKey={`${doc.landsdel}__fitLower`}
                                    stackId={`fitBand_${idx}`}
                                    stroke="none"
                                    fillOpacity={0}
                                    connectNulls={false}
                                    legendType="none"
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey={`${doc.landsdel}__fitBand`}
                                    stackId={`fitBand_${idx}`}
                                    stroke="none"
                                    fill={`url(#fitBand_${doc.landsdel.replace(/[^a-zA-Z0-9_-]/g, "_")}_${idx})`}
                                    connectNulls={false}
                                    legendType="none"
                                  />
                                </>
                              )}
                              <Line
                                type="monotone"
                                dataKey={`${doc.landsdel}__fit`}
                                stroke={chartStrokePalette[idx % chartStrokePalette.length]}
                                dot={false}
                                connectNulls={false}
                                strokeDasharray="4 4"
                                name={`${doc.landsdel} fit`}
                              />
                            </>
                          )}
                        </React.Fragment>
                      ))}
                      {visibleAgroComparableLandsdele.map((landsdel, idx) => {
                        const stroke = ["#9c27b0", "#ef6c00", "#6a1b9a", "#ad1457", "#5e35b1"][idx % 5];
                        return (
                          <Line
                            key={`${landsdel}__agro_line`}
                            type="monotone"
                            dataKey={`${landsdel}__agro`}
                            stroke={stroke}
                            strokeWidth={3}
                            dot={{ r: 2, fill: stroke, stroke: "#ffffff", strokeWidth: 1 }}
                            activeDot={{ r: 4 }}
                            connectNulls={false}
                            strokeDasharray="10 4 2 4"
                            name={`${landsdel} AgroCura (scaled, markers)`}
                          />
                        );
                      })}
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ fontSize: "12px", opacity: 0.8 }}>Matrix view:</div>
                <select
                  value={allMatrixMode}
                  onChange={(evt) => setAllMatrixMode(evt.target.value as "factor" | "txCount" | "fitValue")}
                >
                  <option value="factor">Factor</option>
                  <option value="txCount">Transactions</option>
                  <option value="fitValue">Fitted value</option>
                </select>
              </div>

              <div style={{ marginTop: "12px" }}>
                <div style={{ fontSize: "12px", opacity: 0.85, marginBottom: "6px" }}>Computed matrix</div>
                <div style={{ maxHeight: "420px", overflow: "auto", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "rgba(0,0,0,0.65)" }}>
                      <tr>
                        <Th>Landsdel</Th>
                        {allPeriodHeaders.map((periodKey) => (
                          <Th key={periodKey}>{periodKey}</Th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {documents.map((doc) => {
                        const periodByKey = new Map(doc.periods.map((period) => [period.periodKey, period]));
                        const indexByKey = new Map(doc.periods.map((period, idx) => [period.periodKey, idx]));
                        const fit = allValueFits.get(doc.landsdel);
                        const fittedFactorMap = fittedFactorByLandsdel.get(doc.landsdel) || new Map<string, number | null>();
                        const isVisible = visibleLandsdele.includes(doc.landsdel);
                        return (
                          <tr
                            key={doc.landsdel}
                            onClick={() => toggleLandsdelVisibility(doc.landsdel)}
                            style={{ cursor: "pointer", opacity: isVisible ? 1 : 0.45 }}
                            title={isVisible ? "Click to hide from graph" : "Click to show on graph"}
                          >
                            <Td>
                              <div>{isVisible ? "☑" : "☐"} {doc.landsdel}</div>
                              <div style={{ fontSize: "11px", opacity: 0.75 }}>
                                ROI (fit): {formatRoi(annualRoiByLandsdel.get(doc.landsdel) ?? null)}
                              </div>
                            </Td>
                            {allPeriodHeaders.map((periodKey) => {
                              const period = periodByKey.get(periodKey);
                              if (allMatrixMode === "txCount") {
                                return <Td key={`${doc.landsdel}-${periodKey}`}>{formatCount(period?.txCount ?? null)}</Td>;
                              }
                              if (allMatrixMode === "fitValue") {
                                const idx = indexByKey.get(periodKey);
                                const fitValue = fit && idx !== undefined ? fit.a * Math.exp(fit.b * idx) : null;
                                const fitFactor = fittedFactorMap.get(periodKey) ?? null;
                                return (
                                  <Td key={`${doc.landsdel}-${periodKey}`}>
                                    <div>{formatValue(fitValue)}</div>
                                    <div style={{ fontSize: "11px", opacity: 0.75 }}>{formatFactor(fitFactor)}</div>
                                  </Td>
                                );
                              }
                              return <Td key={`${doc.landsdel}-${periodKey}`}>{formatFactor(period?.factor ?? null)}</Td>;
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div style={{ fontSize: "12px", opacity: 0.85, marginTop: "10px", marginBottom: "6px" }}>
                  AgroCura normalized factor + difference
                  {allMatrixMode === "factor" ? " (vs your factor)" : allMatrixMode === "fitValue" ? " (vs your fitted factor)" : ""}
                </div>
                <div style={{ maxHeight: "420px", overflow: "auto", border: "1px solid rgba(255,255,255,0.1)" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead style={{ position: "sticky", top: 0, background: "rgba(0,0,0,0.65)" }}>
                      <tr>
                        <Th>AgroCura (normalized)</Th>
                        {allPeriodHeaders.map((periodKey) => (
                          <Th key={`agro-${periodKey}`}>{periodKey}</Th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {agroComparableDocuments.map((doc) => {
                        const agro = agroNormalizedByLandsdel.get(doc.landsdel) || {};
                        const localFactorByKey = new Map(doc.periods.map((period) => [period.periodKey, period.factor]));
                        const localFittedFactorByKey = fittedFactorByLandsdel.get(doc.landsdel) || new Map<string, number | null>();
                        const isAgroVisible = visibleAgroLandsdele.includes(doc.landsdel);
                        return (
                          <tr
                            key={`agro-${doc.landsdel}`}
                            onClick={() => toggleAgroVisibility(doc.landsdel)}
                            style={{ cursor: "pointer", opacity: isAgroVisible ? 1 : 0.45 }}
                            title={isAgroVisible ? "Click to hide AgroCura overlay from graph" : "Click to show AgroCura overlay in graph"}
                          >
                            <Td>{isAgroVisible ? "☑" : "☐"} {doc.landsdel}</Td>
                            {allPeriodHeaders.map((periodKey) => {
                              const agroFactor = agro[periodKey] ?? null;
                              const localFactor =
                                allMatrixMode === "factor"
                                  ? localFactorByKey.get(periodKey) ?? null
                                  : allMatrixMode === "fitValue"
                                    ? localFittedFactorByKey.get(periodKey) ?? null
                                    : null;
                              const delta =
                                typeof agroFactor === "number" &&
                                  Number.isFinite(agroFactor) &&
                                  typeof localFactor === "number" &&
                                  Number.isFinite(localFactor)
                                  ? localFactor - agroFactor
                                  : null;
                              const deltaColor =
                                delta === null ? undefined : delta >= 0 ? "#2e7d32" : "#c62828";

                              return (
                                <Td key={`agro-${doc.landsdel}-${periodKey}`}>
                                  <div>{formatFactor(agroFactor)}</div>
                                  {(allMatrixMode === "factor" || allMatrixMode === "fitValue") && (
                                    <div style={{ fontSize: "11px", color: deltaColor }}>
                                      {formatSignedFactor(delta)}
                                    </div>
                                  )}
                                </Td>
                              );
                            })}
                          </tr>
                        );
                      })}
                      {agroComparableDocuments.length === 0 && (
                        <tr>
                          <Td>No AgroCura match for selected landsdele</Td>
                          {allPeriodHeaders.map((periodKey) => (
                            <Td key={`agro-empty-${periodKey}`}>-</Td>
                          ))}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={{ marginTop: "10px", display: "flex", justifyContent: "flex-end" }}>
                <GlassButton onClick={runUpload} disabled={!canUploadComputedAll}>
                  {isUploading ? (
                    <>
                      <Spinner animation="border" size="sm" style={{ width: "1em", height: "1em", marginRight: "6px" }} />
                      Uploading...
                    </>
                  ) : (
                    "Upload"
                  )}
                </GlassButton>
              </div>
            </>
          ) : (
            <div style={{ marginTop: "12px", opacity: 0.75 }}>Run a computation to preview graph and table.</div>
          )}

        </div>
      </ADModalBody>
    </AdvancedDragModal>
  );
}

function Th(props: { children: React.ReactNode }) {
  return <th style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid rgba(255,255,255,0.12)" }}>{props.children}</th>;
}

function Td(props: { children: React.ReactNode }) {
  return <td style={{ textAlign: "left", padding: "8px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>{props.children}</td>;
}

function fitExpForValueSeries(values: Array<number | null>): { a: number; b: number; rSquared: number; rmse: number } | undefined {
  const points = values
    .map((value, idx) => ({ x: idx, y: value }))
    .filter((point) => typeof point.y === "number" && point.y > 0) as { x: number; y: number }[];

  if (points.length < 2) return undefined;

  const xMean = points.reduce((sum, p) => sum + p.x, 0) / points.length;
  const lnY = points.map((p) => Math.log(p.y));
  const yMean = lnY.reduce((sum, value) => sum + value, 0) / lnY.length;

  let numerator = 0;
  let denominator = 0;
  for (let i = 0; i < points.length; i++) {
    numerator += (points[i].x - xMean) * (lnY[i] - yMean);
    denominator += Math.pow(points[i].x - xMean, 2);
  }
  if (denominator <= 0) return undefined;

  const b = numerator / denominator;
  const lnA = yMean - b * xMean;
  const a = Math.exp(lnA);

  let ssRes = 0;
  let ssTot = 0;
  let squaredErrorSum = 0;
  for (let i = 0; i < points.length; i++) {
    const predicted = lnA + b * points[i].x;
    ssRes += Math.pow(lnY[i] - predicted, 2);
    ssTot += Math.pow(lnY[i] - yMean, 2);
    const predictedValue = a * Math.exp(b * points[i].x);
    squaredErrorSum += Math.pow(points[i].y - predictedValue, 2);
  }
  const rSquared = ssTot <= 0 ? 1 : Math.max(0, Math.min(1, 1 - ssRes / ssTot));
  const rmse = Math.sqrt(squaredErrorSum / points.length);

  return { a, b, rSquared, rmse };
}

function formatSeriesLabel(name: string): string {
  if (name.endsWith("__fitBand")) {
    return `${name.replace(/__fitBand$/, "")} fit interval`;
  }
  if (name.endsWith("__fitUpper")) {
    return `${name.replace(/__fitUpper$/, "")} fit upper`;
  }
  if (name.endsWith("__fitLower")) {
    return `${name.replace(/__fitLower$/, "")} fit lower`;
  }
  if (name.endsWith("__fit")) {
    return `${name.replace(/__fit$/, "")} fit`;
  }
  if (name.endsWith("__agro")) {
    return `${name.replace(/__agro$/, "")} AgroCura (scaled, markers)`;
  }
  return name;
}

